// TabBar — underline tabs for in-page navigation (e.g. facility detail's
// Activity / Tasks / Status / Photos tabs). Each tab can show an optional
// red count badge for "things needing attention" (3 open tasks, etc.).
//
// Controlled via `activeId` + `onChange`. Rendered as <button>s for
// accessibility.

import type { ReactNode } from 'react';

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Optional red dot count badge (overdue tasks, etc.). */
  count?: number;
  /** Optional inline icon rendered before the label. */
  icon?: ReactNode;
}

export interface TabBarProps<T extends string = string> {
  tabs: ReadonlyArray<TabItem<T>>;
  activeId: T;
  onChange: (id: T) => void;
  className?: string;
}

export function TabBar<T extends string>({
  tabs,
  activeId,
  onChange,
  className,
}: TabBarProps<T>) {
  return (
    <div
      role="tablist"
      className={`flex gap-1 border-b border-tcs-border ${className ?? ''}`}
    >
      {tabs.map((t) => {
        const active = t.id === activeId;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(t.id)}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors ${
              active
                ? 'border-tcs-text font-medium text-tcs-text'
                : 'border-transparent text-tcs-text-secondary hover:text-tcs-text'
            }`}
          >
            {t.icon}
            <span>{t.label}</span>
            {typeof t.count === 'number' && t.count > 0 && (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-st-danger-solid px-1 text-[10px] font-bold text-st-solid-ink">
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
